import React from 'react';
import { TableRow, TableCell, Typography, Chip, Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { getStatusChipProps } from '../models/Client';
import { formatDaysSinceLastContact, formatLastContactTooltip } from '../utils/contactDate';
import {
  resolveCreatedBy,
  resolveLastContactDate,
  resolveReminderState,
} from '../utils/clientListResolvers';
import ReminderBellStack from './ReminderBellStack';

const TruncatedText = ({ text, maxWidth }) => (
  <Tooltip title={text}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth,
      }}
    >
      {text}
    </Typography>
  </Tooltip>
);

const ClientsTableRow = ({
  client,
  index,
  page,
  pageSize,
  isSelected,
  isListExpanded,
  selectedClientId,
  selectedActivities,
  onRowClick,
  onDismissReminder,
  isDismissing,
}) => {
  const queryClient = useQueryClient();
  const isStriped = index % 2 === 1;
  const reminderState = resolveReminderState(client, queryClient);
  const lastContactDate = resolveLastContactDate(client, {
    selectedClientId,
    selectedActivities,
    queryClient,
  });
  const daysSinceContact = formatDaysSinceLastContact(lastContactDate);
  const lastContactTooltip = formatLastContactTooltip(lastContactDate);

  return (
    <TableRow
      hover
      selected={isSelected}
      onClick={() => onRowClick(client.id)}
      sx={{
        cursor: 'pointer',
        bgcolor: isStriped && !isSelected ? 'grey.50' : undefined,
        '&:hover': {
          bgcolor: isSelected ? 'primary.100' : 'grey.100',
        },
        '&.Mui-selected': {
          bgcolor: 'primary.50',
          '&:hover': { bgcolor: 'primary.100' },
        },
        '&.Mui-selected td:first-of-type': {
          boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.primary.main}`,
        },
      }}
    >
      <TableCell align="center" sx={{ py: 1.5, width: 48, px: 1, color: 'text.secondary' }}>
        <Typography variant="body2">{page * pageSize + index + 1}</Typography>
      </TableCell>
      <TableCell sx={{ py: 1.5 }}>
        <Typography variant="body1" sx={{ fontWeight: isSelected ? 600 : 500 }}>
          {client.companyName}
        </Typography>
      </TableCell>
      <TableCell sx={{ py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {client.city || '—'}
        </Typography>
      </TableCell>
      <TableCell sx={{ py: 1.5 }}>
        {client.status ? (
          <Chip
            label={client.getStatusLabel()}
            size="small"
            onClick={(e) => e.stopPropagation()}
            {...getStatusChipProps(client.status)}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        )}
      </TableCell>
      <TableCell sx={{ py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {resolveCreatedBy(client, queryClient) || '—'}
        </Typography>
      </TableCell>
      <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
        {lastContactTooltip ? (
          <Tooltip title={`Ostatni kontakt: ${lastContactTooltip}`}>
            <Typography variant="body2" color="text.secondary" component="span">
              {daysSinceContact}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {daysSinceContact}
          </Typography>
        )}
      </TableCell>
      {isListExpanded && (
        <>
          <TableCell sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
            <Typography variant="body2" color="text.secondary">
              {client.phoneNumber || '—'}
            </Typography>
          </TableCell>
          <TableCell sx={{ py: 1.5, maxWidth: 200 }}>
            {client.note ? (
              <TruncatedText text={client.note} maxWidth={200} />
            ) : (
              <Typography variant="body2" color="text.secondary">—</Typography>
            )}
          </TableCell>
          <TableCell sx={{ py: 1.5, maxWidth: 240 }}>
            {client.lastComment ? (
              <TruncatedText text={client.lastComment} maxWidth={240} />
            ) : (
              <Typography variant="body2" color="text.secondary">—</Typography>
            )}
          </TableCell>
        </>
      )}
      <TableCell align="center" sx={{ py: 1.5, width: 52, px: 0.5, verticalAlign: 'middle' }}>
        <ReminderBellStack
          hasActiveReminder={reminderState.hasActiveReminder}
          placement="inline"
          onOpenReminder={(event) => {
            event.stopPropagation();
            onRowClick(client.id);
          }}
          onDismissReminder={(event) => onDismissReminder(event, reminderState)}
          isDismissing={isDismissing}
        />
      </TableCell>
    </TableRow>
  );
};

export default ClientsTableRow;
